import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Activity, TrendingUp, LogOut, Shield, ChevronDown, History, FlaskConical, Zap, Settings2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { HelpMenu } from "./HelpMenu";
import { PlanBadge } from "./PlanBadge";
import { setTradeMode } from "../lib/api";

export const Header = () => {
  const { user, logout, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [flipping, setFlipping] = useState(false);
  const now = new Date();

  const flipMode = async () => {
    if (!user?.trading_enabled || flipping) return;
    const next = user.trade_mode === "live" ? "paper" : "live";
    if (next === "live" && !user.tradier_connected) {
      toast.error("Connect Tradier first (Settings → Trading)");
      return;
    }
    if (next === "live" && !window.confirm(`Switch to SUBMIT mode? Real orders will be routed to Tradier${user.tradier_env ? ` (${user.tradier_env})` : ""}.`)) return;
    setFlipping(true);
    try {
      await setTradeMode(next);
      await refresh?.();
      toast.success(`Mode: ${next.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mode change failed");
    } finally { setFlipping(false); }
  };

  return (
    <header
      data-testid="app-header"
      className="border-b border-[#27272A] bg-[#0A0A0A] px-6 py-3 flex items-center justify-between sticky top-0 z-30"
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 border border-[#3F3F46] flex items-center justify-center">
          <TrendingUp size={16} className="text-[#00E676]" strokeWidth={2.5} />
        </div>
        <div>
          <h1 className="text-base font-bold tracking-tight leading-none">
            ROLL<span className="text-[#00E676]">.</span>OPS
          </h1>
          <p className="label-cap mt-1">Covered Call Rolling Terminal</p>
        </div>
      </div>
      <div className="hidden md:flex items-center gap-4">
        {user?.trading_enabled && (
          <button
            data-testid="header-trade-mode-toggle"
            onClick={flipMode}
            disabled={flipping}
            title={
              user.trade_mode === "live"
                ? "SUBMIT mode: orders are sent to Tradier. Click to switch to SIMULATE."
                : "SIMULATE mode: orders stay inside ROLL.OPS, nothing hits Tradier. Click to switch to SUBMIT."
            }
            className={`px-2.5 py-1 border text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5 transition-colors ${
              user.trade_mode === "live"
                ? "border-[#FF3B30] bg-[rgba(255,59,48,0.15)] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.25)]"
                : "border-[#00E676] bg-[rgba(0,230,118,0.08)] text-[#00E676] hover:bg-[rgba(0,230,118,0.15)]"
            } disabled:opacity-50`}
          >
            <Zap size={11} /> {user.trade_mode === "live" ? `SUBMIT · ${(user.tradier_env || "").toUpperCase()}` : "SIMULATE"}
          </button>
        )}
        <HelpMenu />
        <PlanBadge />
        <div className="flex items-center gap-2 text-[11px] font-mono text-[#A1A1AA]">
          <Activity size={12} className="text-[#00E676]" />
          TRADIER · LIVE DATA
        </div>
        <div className="text-[11px] font-mono text-[#A1A1AA]">
          {now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "2-digit" })} ·{" "}
          {now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
        </div>
        {user && (
          <div className="relative">
            <button
              data-testid="user-menu-btn"
              onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-2 px-2 py-1 border border-[#27272A] hover:border-[#3F3F46] transition-colors"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
              ) : (
                <div className="w-6 h-6 bg-[#27272A] flex items-center justify-center text-[10px] font-bold">
                  {user.name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <span className="text-xs font-mono max-w-[140px] truncate">{user.name}</span>
              <ChevronDown size={12} className="text-[#71717A]" />
            </button>
            {open && (
              <div className="absolute right-0 top-full mt-1 w-56 panel z-40">
                <div className="px-3 py-3 border-b border-[#27272A]">
                  <div className="text-xs font-semibold truncate">{user.name}</div>
                  <div className="text-[10px] text-[#A1A1AA] font-mono truncate">{user.email}</div>
                  {user.role === "admin" && (
                    <div className="mt-1 text-[9px] font-bold tracking-widest text-[#00E676] uppercase">Admin</div>
                  )}
                </div>
                <Link
                  to="/history"
                  data-testid="history-link"
                  className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-[#1A1A1A] transition-colors"
                  onClick={() => setOpen(false)}
                >
                  <History size={12} /> Roll History
                </Link>
                <Link
                  to="/chart"
                  data-testid="chart-link"
                  className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-[#1A1A1A] transition-colors"
                  onClick={() => setOpen(false)}
                >
                  <TrendingUp size={12} /> Position Timeline
                </Link>
                <Link
                  to="/backtest"
                  data-testid="backtest-link"
                  className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-[#1A1A1A] transition-colors"
                  onClick={() => setOpen(false)}
                >
                  <FlaskConical size={12} /> Backtest Replay
                </Link>
                {user.trading_enabled && (
                  <Link
                    to="/settings/trading"
                    data-testid="trading-settings-link"
                    className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-[#1A1A1A] transition-colors"
                    onClick={() => setOpen(false)}
                  >
                    <Settings2 size={12} /> Trading Settings
                  </Link>
                )}
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="admin-link"
                    className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-[#1A1A1A] transition-colors"
                    onClick={() => setOpen(false)}
                  >
                    <Shield size={12} /> Admin Console
                  </Link>
                )}
                <button
                  data-testid="logout-btn"
                  onClick={logout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-[#1A1A1A] transition-colors text-left"
                >
                  <LogOut size={12} /> Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
